import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from "@/components/ui/dialog";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { resolveArtworkUrl, CONDITION_COLORS, LOCATION_COLORS, AVAILABILITY_COLORS, COLOUR_SWATCHES, formatDate, formatMoney } from "@/lib/api";
import { Sparkles, Pencil, Trash2, Calendar, Tag, Disc3, Hash, PoundSterling, ShoppingBag, ExternalLink, RefreshCw, Palette, Gem, Zap, ShieldCheck } from "lucide-react";

function Field({ label, value, icon: Icon }) {
    if (value === null || value === undefined || value === "") return null;
    return (
        <div className="flex gap-3">
            {Icon && <Icon className="w-4 h-4 text-muted-foreground mt-1 shrink-0" strokeWidth={1.5} />}
            <div>
                <p className="overline text-muted-foreground">{label}</p>
                <p className="text-sm mt-0.5">{value}</p>
            </div>
        </div>
    );
}

export default function VinylDetailDialog({ record, onOpenChange, onEdit, onDelete, onClean, onResync, onToggleIdp }) {
    if (!record) {
        return (
            <Dialog open={false} onOpenChange={onOpenChange}>
                <DialogContent />
            </Dialog>
        );
    }

    return (
        <Dialog open={!!record} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-card" data-testid="vinyl-detail-dialog">
                <DialogHeader>
                    <DialogDescription className="sr-only">Record details for {record.album_title}</DialogDescription>
                </DialogHeader>
                <div className="grid md:grid-cols-2 gap-8">
                    <div className="aspect-square rounded-md overflow-hidden bg-secondary border border-border">
                        <img
                            src={resolveArtworkUrl(record)}
                            alt=""
                            className="w-full h-full object-cover"
                        />
                    </div>
                    <div className="space-y-4">
                        <div>
                            <p className="overline text-muted-foreground">{record.artist}</p>
                            <DialogTitle asChild>
                                <h2 className="font-display text-4xl font-bold leading-none tracking-tight mt-2">
                                    {record.album_title}
                                </h2>
                            </DialogTitle>
                        </div>
                        <div className="flex flex-wrap gap-2">
                            <span className={`px-3 py-1 text-xs rounded-full border ${LOCATION_COLORS[record.location] || LOCATION_COLORS["Andy"]}`}>
                                {record.location}
                            </span>
                            <span className={`px-3 py-1 text-xs rounded-full border ${CONDITION_COLORS[record.media_condition]}`}>
                                Media: {record.media_condition}
                            </span>
                            <span className={`px-3 py-1 text-xs rounded-full border ${CONDITION_COLORS[record.sleeve_condition]}`}>
                                Sleeve: {record.sleeve_condition}
                            </span>
                            <span className="px-3 py-1 text-xs rounded-full border border-border bg-muted text-muted-foreground font-mono">
                                {record.speed} rpm
                            </span>
                            {record.availability && (
                                <span
                                    className={`px-3 py-1 text-xs rounded-full border ${AVAILABILITY_COLORS[record.availability] || ""}`}
                                    data-testid="detail-availability-badge"
                                >
                                    {record.availability}
                                </span>
                            )}
                            {record.limited && (
                                <span
                                    className="px-3 py-1 text-xs rounded-full border border-[#C0392B]/40 bg-[#C0392B]/10 text-[#C0392B] font-semibold inline-flex items-center gap-1"
                                    data-testid="detail-limited-badge"
                                >
                                    <Zap className="w-3 h-3" strokeWidth={2.5} />
                                    Limited
                                </span>
                            )}
                            {record.colour && (
                                <span
                                    className="px-3 py-1 text-xs rounded-full border border-border bg-card inline-flex items-center gap-1.5"
                                    data-testid="detail-colour-badge"
                                >
                                    <span
                                        className="inline-block w-3 h-3 rounded-full border border-border/60"
                                        style={{ background: COLOUR_SWATCHES[record.colour] || "#111" }}
                                    />
                                    {record.colour}
                                </span>
                            )}
                        </div>

                        {record.discogs_id && (
                            <div className="flex flex-wrap items-center gap-2 pt-1" data-testid="discogs-link-row">
                                <span className="overline text-muted-foreground">Discogs</span>
                                <a
                                    href={record.discogs_url || `https://www.discogs.com/release/${record.discogs_id}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline font-mono"
                                    data-testid="discogs-external-link"
                                >
                                    #{record.discogs_id}
                                    <ExternalLink className="w-3 h-3" />
                                </a>
                                <button
                                    type="button"
                                    onClick={() => onResync(record.id)}
                                    className="ml-1 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-primary transition-colors"
                                    data-testid="resync-discogs-btn"
                                >
                                    <RefreshCw className="w-3 h-3" />
                                    Re-sync
                                </button>
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-4 pt-2">
                            <Field label="Release year" value={record.release_year} icon={Calendar} />
                            <Field label="Genre" value={record.genre} icon={Tag} />
                            <Field label="Label" value={record.label} icon={Disc3} />
                            <Field label="Catalog #" value={record.catalog_number} icon={Hash} />
                            <Field label="Purchase price" value={record.purchase_price != null ? formatMoney(record.purchase_price) : null} icon={PoundSterling} />
                            <Field label="Purchased on" value={formatDate(record.purchase_date)} icon={ShoppingBag} />
                            <Field label="Last cleaned" value={record.date_last_cleaned ? formatDate(record.date_last_cleaned) : "Never"} icon={Sparkles} />
                            <Field label="Colour" value={record.colour} icon={Palette} />
                            <Field label="Availability" value={record.availability} icon={Gem} />
                        </div>

                        {record.notes && (
                            <div className="pt-2">
                                <p className="overline text-muted-foreground mb-1">Notes</p>
                                <p className="text-sm leading-relaxed whitespace-pre-wrap">{record.notes}</p>
                            </div>
                        )}

                        <div
                            className="flex items-center justify-between rounded-md border border-border bg-secondary/40 px-3 py-2.5"
                            data-testid="idp-row"
                        >
                            <div className="flex items-start gap-2">
                                <ShieldCheck className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0" strokeWidth={1.5} />
                                <div>
                                    <Label className="overline">IDP</Label>
                                    <p className="text-xs text-muted-foreground mt-0.5">
                                        {record.idp
                                            ? "Insurance report uses this record's purchase price"
                                            : "Insurance report uses live Discogs pricing"}
                                    </p>
                                </div>
                            </div>
                            <Switch
                                checked={!!record.idp}
                                onCheckedChange={(v) => onToggleIdp && onToggleIdp(record.id, v)}
                                className={record.idp ? "data-[state=checked]:bg-primary" : ""}
                                aria-label="Toggle Insurance Default Price"
                                data-testid="idp-toggle"
                            />
                        </div>

                        <div className="flex flex-wrap gap-2 pt-4 border-t border-border">
                            <Button
                                onClick={() => onClean(record.id)}
                                variant="outline"
                                className="gap-2"
                                data-testid="clean-now-btn"
                            >
                                <Sparkles className="w-4 h-4" />
                                Cleaned today
                            </Button>
                            <Button
                                onClick={() => onEdit(record)}
                                variant="outline"
                                className="gap-2"
                                data-testid="edit-record-btn"
                            >
                                <Pencil className="w-4 h-4" />
                                Edit
                            </Button>
                            <AlertDialog>
                                <AlertDialogTrigger asChild>
                                    <Button
                                        variant="outline"
                                        className="gap-2 text-destructive hover:bg-destructive/10 hover:text-destructive ml-auto"
                                        data-testid="delete-record-btn"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                        Delete
                                    </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                    <AlertDialogHeader>
                                        <AlertDialogTitle>Remove this record?</AlertDialogTitle>
                                        <AlertDialogDescription>
                                            This will permanently remove &ldquo;{record.album_title}&rdquo; from the collection.
                                        </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                        <AlertDialogCancel data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
                                        <AlertDialogAction
                                            onClick={() => onDelete(record.id)}
                                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                            data-testid="confirm-delete-btn"
                                        >
                                            Delete
                                        </AlertDialogAction>
                                    </AlertDialogFooter>
                                </AlertDialogContent>
                            </AlertDialog>
                        </div>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
